import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import ExpertLayout from '../../components/ExpertLayout';
import PageHeader from '../../components/expert/PageHeader';
import { expertDashboardApi, getErrorMessage } from '../../lib/http';
import { ExpertProfile } from '../../types';
import { ProfileSettings } from '../../components/expert/settings/ProfileSettings';
import { PublicLinkSettings } from '../../components/expert/settings/PublicLinkSettings';
import { ScheduleSettings } from '../../components/expert/settings/ScheduleSettings';
import { BookingPreferencesSettings } from '../../components/expert/settings/BookingPreferencesSettings';

const ExpertSettings: React.FC = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<ExpertProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoading(true);
        const response = await expertDashboardApi.profile.get();
        setProfile(response.data);
      } catch (err: any) {
        if (err.response?.status === 401) {
          navigate('/expert/login');
          return;
        }
        setError(getErrorMessage(err));
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, [navigate]);

  const handleProfileUpdate = (updatedProfile: ExpertProfile) => {
    setProfile(updatedProfile);
  };

  const handleRetry = () => {
    setError(null);
    setIsLoading(true);
    expertDashboardApi.profile.get()
      .then(response => {
        setProfile(response.data);
        setError(null);
      })
      .catch(err => {
        setError(getErrorMessage(err));
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <ExpertLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <PageHeader
          title="Settings"
          subtitle="Manage your account preferences and public booking link"
          backTo="/app/dashboard"
        />

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <Loader2 className="w-10 h-10 text-pink-600 animate-spin mx-auto" />
              <p className="mt-4 text-gray-600">Loading settings...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-6">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="h-5 w-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-red-800">Error loading settings</h3>
                <p className="mt-1 text-sm text-red-700">{error}</p>
                <button
                  onClick={handleRetry}
                  className="mt-3 text-sm font-medium text-red-600 hover:text-red-700"
                >
                  Try again &rarr;
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Settings Content */}
        {profile && !isLoading && !error && (
          <div className="max-w-3xl">
            {/* Settings Sections */}
            <div className="space-y-6">
              {/* Profile Settings */}
              <ProfileSettings
                profile={profile}
                onUpdate={handleProfileUpdate}
              />

              {/* Booking Preferences (timezone + availability) */}
              <BookingPreferencesSettings
                profile={profile}
                onUpdate={handleProfileUpdate}
              />

              {/* Public Link Settings */}
              <PublicLinkSettings
                profile={profile}
                onUpdate={handleProfileUpdate}
              />

              {/* Schedule Settings */}
              <ScheduleSettings
                profile={profile}
                onUpdate={handleProfileUpdate}
              />
            </div>
          </div>
        )}
      </div>
    </ExpertLayout>
  );
};

export default ExpertSettings;
